import React, { Children } from 'react';
import PropTypes from 'prop-types';
import useEnvironmentalizeLink from '@lmig/dotcom-aspect-hooks/useEnvironmentalizeLink';
import useLinkAppend from '@lmig/dotcom-aspect-hooks/useLinkAppend';

/**
 * Allows a link to be environmentalized by application and the environment
 * the application is deployed to.
 *
 * @example
 * // <a href="https://dev-www2.libertymutual.com/auto">Auto Page</a>
 * <LinkEnvironmentalize domain="LM_COM">
 *   <a href="/auto">Auto Page</a>
 * </LinkEnvironmentalize>
 *
 */
const LinkEnvironmentalize = ({
  link = '',
  children = undefined,
  domain = undefined,
  analyticsAction = 'action url',
  attribution = false,
}) => {
  if (typeof children === 'string') {
    children = <a>{children}</a>; // eslint-disable-line no-param-reassign
  }

  // To make this component backwards-compatible and _not_ require being wrapped in context providers
  //   we need to try/catch the hook call so we can intercept the thrown error. We only want to throw
  //   an error when attribution is required and we need the context provider values.
  let appendParams = s => s;

  try {
    const funcs = useLinkAppend();
    appendParams = funcs.appendParams; // eslint-disable-line prefer-destructuring
  } catch {
    if (attribution) {
      throw new Error(
        "You're trying to use <LinkEvironmentalize> with attribution outside of <ParameterContextProvider>",
      );
    }
  }

  const child = Children.only(children);

  const linkHref = React.isValidElement(child) && child.props.href ? child.props.href : link;

  const href = attribution ? appendParams(linkHref) : linkHref;

  const linkProps = {
    'data-action': analyticsAction || undefined,
    ...child.props,
    href: useEnvironmentalizeLink({ link: href, domain }),
  };

  return React.cloneElement(child, linkProps);
};

LinkEnvironmentalize.propTypes = {
  /** Link being environmentalized. ex: /auto */
  link: PropTypes.string,
  children: PropTypes.node,
  /** Token to get correct domain */
  domain: PropTypes.oneOf([
    'LM_COM',
    '',
    'LOGIN',
    'ESERVICE',
    'LMG',
    'LMG_BUSINESS',
    'LUX',
    'QUOTE_LEAD',
    'QUOTE_LIFE',
    'QUOTE_PET',
    'IMAGE',
    'MOTO',
    'MERTZ',
    'WELCOME',
    'FAC',
    'GLASS',
    'SMS',
    'CLEARSURANCE',
    'COMPARION',
    'CLAIMS',
    'BUSINESS_PROFILE',
    'AGENT',
    'BUSINESS_LOGIN',
    'LIBERTY_PLUS'
  ]),
  /** Analytics tag */
  analyticsAction: PropTypes.string,
  /** Whether the link should contain query parameters for tracking/quote attribution */
  attribution: PropTypes.bool,
};

export default LinkEnvironmentalize;
